/**
 * TodoCallCell + TodoBookingRule — CD-25: "which call is this, and what are the
 * rules?"
 *
 * The design handoff (§1, META row) puts a **Call** cell next to Type for
 * `schedule_call` to-dos: "Which call is being booked. Options (app data)."
 * That one cell is the whole manual-scheduling feature — picking a call type is
 * what lets the composer mint THAT call's booking link on the right calendar,
 * instead of opening a blank email.
 *
 * `TodoBookingRule` is the honest half. A call type can carry a
 * `project_date_floor_field` — the project date the call may not precede. For
 * the Delivery Call that field is `deliver_by`, and the effective floor is
 * deliver-by **+ 1 business day** (the build wraps on deliver-by). That rule
 * has always been enforced server-side for the automated Stage Playbook to-do
 * and was invisible everywhere. Now the container states it: which date it
 * reads, what the client's earliest slot actually is, and — when it can't be
 * applied — one plain sentence saying what to do about it.
 *
 * The design's guidance that the Call cell is "the pattern for future per-type
 * configuration: one type-scoped cell in the meta row, not a new section" is
 * why the rule readout lives in the BODY rather than growing this cell.
 */
import React, { useRef } from 'react';
import styles from './todoContainer.module.css';
import TodoMenu, { type TodoMenuOption } from './TodoMenu';
import type { TodoSchedulingInfo } from '../../services/todosApi';
import { useAuth } from '../../contexts/AuthContext';
import { useCallLinks, callLinkOptions, type CallLink } from './useCallLinks';

// ── The meta-row cell ────────────────────────────────────────────────

export interface TodoCallCellProps {
  /** The bound LINK (`event_type_id` — a person × a call), or null. */
  value: number | null;
  /** What the chip reads: "Documentation Call · Megan Smith". */
  valueName: string | null;
  /** The picked link. It carries its call type (`event_template_id`) and host. */
  onPick: (link: CallLink | null) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  disabled?: boolean;
}

/**
 * 2026-09-24: the cell picks a LINK, not a call type — every teammate's calls,
 * grouped by person and searchable — so whoever books names the calendar
 * outright (see `useCallLinks`).
 */
export const TodoCallCell: React.FC<TodoCallCellProps> = ({
  value, valueName, onPick, open, onToggle, onClose, disabled,
}) => {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const { user } = useAuth();
  const { links } = useCallLinks(true);
  const options: TodoMenuOption[] = links === null
    ? [{ value: '__loading', label: 'Loading booking links…', disabled: true }]
    : callLinkOptions(links, user?.id ?? null);

  return (
    <div className={styles.metaCell}>
      <div className={styles.metaLabel}>Call</div>
      <button
        ref={anchor}
        type="button"
        className={valueName ? styles.chip : styles.chipDashed}
        onClick={onToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Which call, on whose calendar?"
      >
        <span className={valueName ? styles.chipName : undefined}>{valueName || 'Pick a call'}</span>
        <span className={styles.chipCaret}>▾</span>
      </button>
      <TodoMenu
        open={open}
        onClose={onClose}
        anchorRef={anchor}
        header="Which call, on whose calendar?"
        options={options}
        value={value}
        onPick={(v) => {
          if (v == null || v === '__loading') { onPick(null); return; }
          onPick((links || []).find((l) => l.event_type_id === Number(v)) ?? null);
        }}
        width="links"
        filterPlaceholder="Search a call or a person…"
        emptyLabel="No booking links yet — publish one under Scheduling → Event types"
      />
    </div>
  );
};

// ── The rule readout ─────────────────────────────────────────────────

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  // Date-only strings are calendar dates; parse as local so a UTC-midnight
  // instant can't display as the previous day.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export const TodoBookingRule: React.FC<{ info: TodoSchedulingInfo | null }> = ({ info }) => {
  if (!info || (!info.call_type && !info.event_type_id && !info.blocker)) return null;

  const warn = !info.ready;
  return (
    <>
      <div className={styles.sectionHead}>
        <span className={warn ? styles.diamondPaprika : styles.diamondIndigo} />
        <span className={styles.sectionTitle}>Booking rules</span>
      </div>
      <div className={`${styles.ruleCard} ${warn ? styles.ruleCardWarn : ''}`}>
        {info.call_type_name && (
          <div className={styles.ruleLine}>
            <span className={styles.ruleTerm}>Books</span>
            <span className={styles.ruleValue}>
              {info.call_type_name}
              {info.duration_minutes ? ` · ${info.duration_minutes} min` : ''}
              {/* Where the binding came from. A Stage Playbook to-do can resolve
                  its call by TITLE without carrying its own meta — the composer
                  honours that, so saying "from the stage playbook" is more
                  honest than showing the cell as unset. */}
              {info.host_name && (
                <span style={{ color: 'var(--ink-faint)', fontWeight: 400 }}> · {info.host_name}’s calendar</span>
              )}
              {info.from_playbook && (
                <span style={{ color: 'var(--ink-faint)', fontWeight: 400 }}> · from the stage playbook</span>
              )}
            </span>
          </div>
        )}
        {info.project_title && (
          <div className={styles.ruleLine}>
            <span className={styles.ruleTerm}>Project</span>
            <span className={styles.ruleValue}>{info.project_title}</span>
          </div>
        )}
        {info.floor_field_label && (
          <div className={styles.ruleLine}>
            <span className={styles.ruleTerm}>{info.floor_field_label}</span>
            <span className={info.floor_date ? styles.ruleValue : styles.ruleBlocker}>
              {info.floor_date ? fmtDate(info.floor_date) : 'not set'}
            </span>
          </div>
        )}
        {info.earliest_bookable && (
          <div className={styles.ruleLine}>
            <span className={styles.ruleTerm}>Client books</span>
            <span className={styles.ruleValue}>{fmtDate(info.earliest_bookable)} or later</span>
          </div>
        )}
        {info.floor_rule && (
          <div style={{ marginTop: 7, color: 'var(--ink-faint)', fontSize: 11, lineHeight: 1.5 }}>
            {info.floor_rule}
          </div>
        )}
        {info.blocker && (
          <div className={styles.ruleLine} style={{ marginTop: 7 }}>
            <span className={styles.ruleTerm}>To fix</span>
            <span className={styles.ruleBlocker}>{info.blocker}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default TodoCallCell;
